// Edit, pause or resume, and archive, for the group's owner only (S112): admins read other users'
// groups but never change them (the API answers 403). Archive asks first: it is for good, and
// the group's queued tasks are cancelled.
import { Archive, Pause, PencilSimple, Play } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "rizzui";

import { useT } from "../../i18n";
import { toast } from "../../layout/toast";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { archiveTaskGroup, pauseTaskGroup, resumeTaskGroup } from "../api";
import type { TaskGroup } from "../model";

export function GroupActions({ group }: { group: TaskGroup }) {
  const t = useT().tasks.group.actions;
  const queryClient = useQueryClient();
  const [archiving, setArchiving] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["task-groups"] });

  async function run(change: (id: string) => Promise<unknown>, done: string) {
    try {
      await change(group.id);
      await refresh();
      toast(done);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "warn");
    }
  }

  if (group.status === "archived") return null;
  return (
    <>
      <Link to={`/task-groups/${group.id}/edit`} className="inline-flex">
        <Button variant="outline" className="gap-1.5" tabIndex={-1}>
          <PencilSimple weight="bold" className="h-4 w-4" /> {t.edit}
        </Button>
      </Link>
      {group.status === "active" ? (
        <Button variant="outline" className="gap-1.5" onClick={() => void run(pauseTaskGroup, t.paused)}>
          <Pause weight="bold" className="h-4 w-4" /> {t.pause}
        </Button>
      ) : (
        <Button variant="outline" className="gap-1.5" onClick={() => void run(resumeTaskGroup, t.resumed)}>
          <Play weight="bold" className="h-4 w-4" /> {t.resume}
        </Button>
      )}
      <Button variant="outline" color="danger" className="gap-1.5" onClick={() => setArchiving(true)}>
        <Archive weight="bold" className="h-4 w-4" /> {t.archive}
      </Button>
      <ConfirmDialog
        open={archiving}
        label={t.archiveConfirm(group.name)}
        title={t.archiveConfirm(group.name)}
        confirmLabel={t.archive}
        onClose={() => setArchiving(false)}
        onConfirm={async () => {
          await archiveTaskGroup(group.id);
          await refresh();
          toast(t.archived(group.name));
        }}
      >
        {t.archiveBody}
      </ConfirmDialog>
    </>
  );
}
